import { roundSymbolFor, roundSymbols } from './broker';
import { directionDigit } from './digits';
/**
 * Timed rounds. Each modality runs its own global clock, derived only from the
 * epoch so the API, workers and every browser agree without coordination:
 *
 *   |-- BETTING --|-- RUNNING (real market) --|-- BETTING --| ...
 *   betStart    openAt                      endAt (= next betStart)
 *
 *   Dígitos:    10s + 5s = a result every 15s (market only on Polarium: no digit contract).
 *   Sobe/Desce: 10s + 10s = a result every 20s (on :00, :20 and :40), traded as 10s Blitz
 *               options bought in the open second.
 *
 * Round ids are per clock: the same id means different times in each modality.
 * Entries are accepted during BETTING (minus a short CLOSING margin used to
 * send the orders). Results come from the per-second ticks
 * of each clock's asset (see broker.ts; tick E = price at the instant E, packages/polarium/src/ticks.ts):
 *   - Sobe/Desce: tick at openAt (the options' entry quote) vs tick at endAt (expiry).
 *   - Dígitos: the same move on its own clock; the digit shown follows it (directionDigit).
 */

export type RoundKind = 'RISE_FALL' | 'EVEN_ODD';
export const ROUND_KINDS: readonly RoundKind[] = ['EVEN_ODD', 'RISE_FALL'];

export interface RoundClockSpec {
  betSeconds: number;
  runSeconds: number;
  cycleSeconds: number;
  /** Entries close this long before the round opens; orders are sent in this gap. */
  closingMs: number;
}

export const ROUND_CLOCKS: Record<RoundKind, RoundClockSpec> = {
  // Dígitos (2026-10-28, was 20s since 10-23): a result every 15s — 8s of open entries + 2s sending them
  // to Polarium, then the 5s round, the length of the Blitz bought behind it.
  EVEN_ODD: { betSeconds: 10, runSeconds: 5, cycleSeconds: 15, closingMs: 2_000 },
  // Sobe e Desce (2026-10-28, was 15s + 15s): a result every 20s — 8s of open entries + 2s sending them
  // to Polarium, then the 10s round, the length of the Blitz bought behind it.
  RISE_FALL: { betSeconds: 10, runSeconds: 10, cycleSeconds: 20, closingMs: 2_000 },
};

/** Clock a modality trades on: Sobe/Desce's, or the digits clock (every digit modality). */
export function roundKind(modality: string): RoundKind {
  return modality === 'RISE_FALL' ? 'RISE_FALL' : 'EVEN_ODD';
}

export type RoundPhase = 'BETTING' | 'CLOSING' | 'RUNNING';

export interface RoundInfo {
  kind: RoundKind;
  /** Round number on this kind's clock. */
  id: number;
  /** Epoch seconds. */
  betStart: number;
  openAt: number;
  endAt: number;
  phase: RoundPhase;
  /** Epoch ms when the current phase ends. */
  phaseEndsAtMs: number;
}

export type RoundWindow = Pick<RoundInfo, 'kind' | 'id' | 'betStart' | 'openAt' | 'endAt'>;

export function roundById(kind: RoundKind, id: number): RoundWindow {
  const c = ROUND_CLOCKS[kind];
  const betStart = id * c.cycleSeconds;
  return { kind, id, betStart, openAt: betStart + c.betSeconds, endAt: betStart + c.cycleSeconds };
}

/** Id of the round whose cycle contains epoch second `epoch`. */
export function roundIdAt(kind: RoundKind, epoch: number): number {
  return Math.floor(epoch / ROUND_CLOCKS[kind].cycleSeconds);
}

/** The round whose cycle contains `nowMs`, and its phase. */
export function roundAt(kind: RoundKind, nowMs: number): RoundInfo {
  const r = roundById(kind, roundIdAt(kind, nowMs / 1000));
  const closeMs = r.openAt * 1000 - ROUND_CLOCKS[kind].closingMs;
  if (nowMs < closeMs) return { ...r, phase: 'BETTING', phaseEndsAtMs: closeMs };
  if (nowMs < r.openAt * 1000) return { ...r, phase: 'CLOSING', phaseEndsAtMs: r.openAt * 1000 };
  return { ...r, phase: 'RUNNING', phaseEndsAtMs: r.endAt * 1000 };
}

/** Round currently accepting entries, or null while entries are closed. */
export function bettingRound(kind: RoundKind, nowMs: number): RoundInfo | null {
  const r = roundAt(kind, nowMs);
  return r.phase === 'BETTING' ? r : null;
}

/** Round that the next entry will go into (current if betting, else the next one). */
export function upcomingRound(kind: RoundKind, nowMs: number): RoundWindow {
  const r = roundAt(kind, nowMs);
  return r.phase === 'BETTING' ? r : roundById(kind, r.id + 1);
}

/** Only the configured round assets (one tick per second from their quotes). */
export function isRoundSymbol(symbol: string): boolean {
  return roundSymbols().includes(symbol);
}

/** The asset a modality's rounds run on: Sobe e Desce or Dígitos (and the old Par/Ímpar). */
export function roundSymbolOf(modality: string): string {
  return roundSymbolFor(roundKind(modality));
}

export type RoundMove = 'UP' | 'DOWN' | 'FLAT';

/** Published once per round per symbol and clock, from real ticks. */
export interface RoundResult {
  kind: RoundKind;
  id: number;
  symbol: string;
  openAt: number;
  endAt: number;
  open: number | null;
  close: number;
  pipSize: number;
  /** Shown digit: even when the round rose, odd when it fell, any when flat (directionDigit). */
  digit: number;
  /** close vs open; null when the opening tick is missing. */
  move: RoundMove | null;
  /**
   * A played round whose two sides Polarium opened at different prices (see RoundReference): each side's
   * opening and what it paid. `move` is then UP or DOWN only when one side won and the other lost;
   * any other split (one lost and one was returned, both lost) reads as FLAT: nobody won.
   */
  sides?: RoundSides;
}

/** What one side of a round paid: Blitz wins above (Sobe) or below (Desce) its entry, returns it on an equal exit. */
export type SideResult = 'WIN' | 'LOSS' | 'REFUND';

export interface RoundSides {
  /** Null only on a player's own reading whose entry price is not known yet (see paidOutcome). */
  rise: { open: number | null; result: SideResult };
  fall: { open: number | null; result: SideResult };
}

export function roundMove(open: number | null, close: number): RoundMove | null {
  if (open === null) return null;
  return close > open ? 'UP' : close < open ? 'DOWN' : 'FLAT';
}

/**
 * A played round's official openings (owner's decisions 2026-10-23): the entry price Polarium gave the
 * round's first entry on each side. Polarium enters a Blitz a fraction of a second after the round opens
 * and exits on the round's closing second at one price for everyone (786 real entries: the exit always
 * matched across a round). The entry is not the same on both sides: bought at the same instant, Sobe
 * (CALL) got a higher price than Desce (PUT) on 76 of 207 pairs and never a lower one — so a close
 * between the two openings pays neither side. Read per side, the round shows what Polarium paid
 * (785 of 786; the other was a second Sobe of the same round that got another price). A side nobody
 * played reads from the other side's opening; rounds nobody played keep the tick opening.
 */
export interface RoundReference {
  kind: RoundKind;
  id: number;
  rise: number | null;
  fall: number | null;
}

/** What a side paid with this opening and close. */
export function sideResult(direction: 'RISE' | 'FALL', open: number, close: number): SideResult {
  if (close === open) return 'REFUND';
  return (close > open) === (direction === 'RISE') ? 'WIN' : 'LOSS';
}

/**
 * Move, digit and (when the sides opened apart) each side's result of a round read from its official
 * openings (see RoundReference). The digit stays seeded by the round id. Null when neither is known.
 */
export function referenceOutcome(id: number, opens: { rise: number | null; fall: number | null }, close: number): { open: number; move: RoundMove; digit: number; sides?: RoundSides } | null {
  const rise = opens.rise ?? opens.fall;
  const fall = opens.fall ?? opens.rise;
  if (rise === null || fall === null) return null;
  const r = sideResult('RISE', rise, close);
  const f = sideResult('FALL', fall, close);
  const move = sidesMove(r, f);
  const out = { open: rise, move, digit: directionDigit(move, id) };
  return rise === fall ? out : { ...out, sides: { rise: { open: rise, result: r }, fall: { open: fall, result: f } } };
}

/** Move of a round from what each side paid: UP/DOWN when one side won and the other lost; FLAT otherwise. */
export function sidesMove(rise: SideResult, fall: SideResult): RoundMove {
  return rise === 'WIN' && fall === 'LOSS' ? 'UP' : rise === 'LOSS' && fall === 'WIN' ? 'DOWN' : 'FLAT';
}

/**
 * A round whose two sides don't read as one move (see RoundReference): the close fell between their
 * openings, so nobody won (one lost, the other was returned, or both lost) — or, with the openings the
 * other way round, both won.
 */
export function isSplitRound(r: { move: RoundMove | null; sides?: { rise: { result: SideResult }; fall: { result: SideResult } } }): boolean {
  return r.move === 'FLAT' && !!r.sides && !(r.sides.rise.result === 'REFUND' && r.sides.fall.result === 'REFUND');
}

/** What a side paid, from the profit Polarium reported for an entry. */
export function paidResult(profit: string | number | null): SideResult {
  const p = Number(profit ?? 0);
  return p > 0 ? 'WIN' : p < 0 ? 'LOSS' : 'REFUND';
}

/** A player's settled entry in a round: its side, what Polarium paid and the price it entered at. */
export interface PaidEntry {
  direction: string | null;
  profit: string | number | null;
  entry: number | null;
}

/**
 * A player's round read from their own money (owner's decision 2026-10-23: the screen never contradicts
 * what was paid). Every settled entry counts, on both sides:
 *   - one side played: its result is the move (Sobe won = UP; Desce won = DOWN; returned = FLAT);
 *   - both sides: UP/DOWN when one won and the other lost, else FLAT with `sides` (see isSplitRound);
 *   - `mixed`: entries of the SAME side paid differently (Polarium gave them different entry prices) —
 *     no single move is true, the screen shows each entry. Null when no entry has a side.
 */
export function paidOutcome(entries: readonly PaidEntry[]): { move: RoundMove; sides?: RoundSides; mixed: boolean } | null {
  const of = (d: 'RISE' | 'FALL') => entries.filter((e) => e.direction === d);
  const rise = of('RISE');
  const fall = of('FALL');
  if (!rise.length && !fall.length) return null;
  const uniform = (l: PaidEntry[]) => (l.length && l.every((e) => paidResult(e.profit) === paidResult(l[0]!.profit)) ? paidResult(l[0]!.profit) : null);
  const r = uniform(rise);
  const f = uniform(fall);
  const mixed = (rise.length > 0 && r === null) || (fall.length > 0 && f === null);
  if (mixed) return { move: 'FLAT', mixed: true };
  if (r !== null && f !== null) {
    const move = sidesMove(r, f);
    const sides: RoundSides = { rise: { open: rise[0]!.entry, result: r }, fall: { open: fall[0]!.entry, result: f } };
    return isSplitRound({ move, sides }) ? { move, sides, mixed: false } : { move, mixed: false };
  }
  if (r !== null) return { move: r === 'WIN' ? 'UP' : r === 'LOSS' ? 'DOWN' : 'FLAT', mixed: false };
  return { move: f === 'WIN' ? 'DOWN' : f === 'LOSS' ? 'UP' : 'FLAT', mixed: false };
}

/** What the round shown to everyone says a side paid: per side when the openings differ, else from the move. */
export function shownResult(r: Pick<RoundResult, 'move' | 'sides'>, direction: 'RISE' | 'FALL'): SideResult | null {
  if (r.sides) return direction === 'RISE' ? r.sides.rise.result : r.sides.fall.result;
  if (!r.move) return null;
  if (r.move === 'FLAT') return 'REFUND';
  return (r.move === 'UP') === (direction === 'RISE') ? 'WIN' : 'LOSS';
}

/**
 * Entries one account can have in a round: Polarium refuses more than 5 deals per second on one
 * account ("too many deals per second", status 4121, live test 2026-10-23) and every entry of a round
 * is bought in the round's open second. Counted per send second, robot entries included.
 */
export const MAX_ROUND_ENTRIES = 5;

/** A round re-read from its official openings (see RoundReference). */
export function withRoundReference(r: RoundResult, opens: { rise: number | null; fall: number | null }): RoundResult {
  const o = referenceOutcome(r.id, opens, r.close);
  if (!o) return r;
  const { sides: _drop, ...base } = r;
  return { ...base, ...o };
}

/** Deriv era: ticks a 1-tick digit contract exited after its entry. Kept for the calibration still stored in Redis. */
export const DIGIT_EXIT_OFFSET = 0;

/**
 * Second a round's entries are bought. On Polarium both clocks buy Blitz options, whose expiry
 * counts from the purchase: bought the second the round opens, a Blitz of the round's length
 * ends with it. (The digit exit offset was Deriv's 1-tick digit contract; it no longer applies.)
 */
export function roundSendSecond(kind: RoundKind, id: number): number {
  return roundById(kind, id).openAt;
}
